/**
 * Device widget wrappery — self-contained: vezmou `slug` z kontextu/configu, telemetrii čtou
 * z device hooku (page dělá WS subscription + snapshot). Delegují na existující komponenty.
 */

import React from "react";
import { usePipDevice } from "@client/hooks/usePip";
import { useTracerDevice } from "@client/hooks/useTracer";
import { useBmsDevice } from "@client/hooks/useBms";
import { PipAlarmsBar } from "@client/components/pip/PipAlarmsBar";
import { PipCharts } from "@client/components/pip/PipCharts";
import { PipControls } from "@client/components/pip/PipControls";
import { PipDeviceInfoCard } from "@client/components/pip/PipDeviceInfo";
import { PipFlowDiagram } from "@client/components/pip/PipFlowDiagram";
import { PipOverviewCard } from "@client/components/pip/PipOverviewCard";
import { PipSettingsForm } from "@client/components/pip/PipSettingsForm";
import { TracerAlarmsBar } from "@client/components/tracer/TracerAlarmsBar";
import { TracerCharts } from "@client/components/tracer/TracerCharts";
import { TracerControls } from "@client/components/tracer/TracerControls";
import { TracerDeviceInfoCard } from "@client/components/tracer/TracerDeviceInfo";
import { TracerFlowDiagram } from "@client/components/tracer/TracerFlowDiagram";
import { TracerOverviewCard } from "@client/components/tracer/TracerOverviewCard";
import { TracerSettingsForm } from "@client/components/tracer/TracerSettingsForm";
import { TracerStatsCards } from "@client/components/tracer/TracerStatsCards";
import { BmsAlarmsBar } from "@client/components/bms/BmsAlarmsBar";
import { BmsCellGrid } from "@client/components/bms/BmsCellGrid";
import { BmsCharts } from "@client/components/bms/BmsCharts";
import { BmsControls } from "@client/components/bms/BmsControls";
import { BmsDeviceInfo } from "@client/components/bms/BmsDeviceInfo";
import { BmsOverviewCard } from "@client/components/bms/BmsOverviewCard";
import { BmsSettingsForm } from "@client/components/bms/BmsSettingsForm";

const NoSlug: React.FC = () => <div className="p-2 text-xs text-muted-foreground">Bez zařízení (slug)</div>;

// ── PIP ──
export const PipWidget: React.FC<{ slug: string; part: string; compact?: boolean }> = ({ slug, part, compact }) => {
  const { device, telemetry } = usePipDevice(slug);
  if (!slug || !device) return <NoSlug />;
  const tel = telemetry ?? device.runtime?.telemetry ?? undefined;
  switch (part) {
    case "overview": return <PipOverviewCard slug={slug} telemetry={tel} compact={compact} />;
    case "flow": return <PipFlowDiagram slug={slug} telemetry={tel} />;
    case "charts": return <PipCharts slug={slug} telemetry={tel} />;
    case "alarms": return <PipAlarmsBar telemetry={tel} />;
    case "controls": return <PipControls slug={slug} settings={device.runtime?.settings ?? null} />;
    case "settings": return <PipSettingsForm slug={slug} record={device} settings={device.runtime?.settings ?? null} />;
    case "deviceInfo": return <PipDeviceInfoCard record={device} deviceInfo={device.runtime?.deviceInfo ?? null} />;
    default: return null;
  }
};

// ── Tracer ──
export const TracerWidget: React.FC<{ slug: string; part: string; compact?: boolean }> = ({ slug, part, compact }) => {
  const { device, telemetry } = useTracerDevice(slug);
  if (!slug || !device) return <NoSlug />;
  const tel = telemetry ?? device.runtime?.telemetry ?? undefined;
  switch (part) {
    case "overview": return <TracerOverviewCard slug={slug} telemetry={tel} compact={compact} />;
    case "flow": return <TracerFlowDiagram slug={slug} telemetry={tel} />;
    case "charts": return <TracerCharts slug={slug} telemetry={tel} />;
    case "alarms": return <TracerAlarmsBar telemetry={tel} />;
    case "statsCards": return <TracerStatsCards slug={slug} statistics={device.runtime?.statistics ?? null} />;
    case "controls": return <TracerControls slug={slug} telemetry={tel} />;
    case "settings": return <TracerSettingsForm slug={slug} settings={device.runtime?.settings ?? null} />;
    case "deviceInfo": return <TracerDeviceInfoCard record={device} deviceInfo={device.runtime?.deviceInfo ?? null} />;
    default: return null;
  }
};

// ── BMS ──
export const BmsWidget: React.FC<{ slug: string; part: string }> = ({ slug, part }) => {
  const { device, telemetry } = useBmsDevice(slug);
  if (!slug || !device) return <NoSlug />;
  switch (part) {
    case "overview": return <BmsOverviewCard slug={slug} telemetry={telemetry} />;
    case "charts": return <BmsCharts slug={slug} telemetry={telemetry} />;
    case "cellGrid": return <BmsCellGrid slug={slug} cells={telemetry?.cells} cellCount={device.cellCount} />;
    case "alarms": return <BmsAlarmsBar alarms={telemetry?.alarms} />;
    case "controls": return <BmsControls slug={slug} mosfet={telemetry?.mosfet} />;
    case "deviceInfo": return <BmsDeviceInfo record={device} />;
    case "settings": return <BmsSettingsForm slug={slug} settings={device.runtime?.settings ?? null} />;
    default: return null;
  }
};
